import { useEffect, useState, type FormEvent } from 'react'
import { Head, useForm, usePage } from '@inertiajs/react'
import type {
  CompanyEnrichment,
  CompanyEnrichmentProposed,
  CompanyEnrichmentQuestion,
  CompanyProfile,
  WorkspaceSetup,
} from '@/types/company'
import type { SharedPageProps } from '@/types/shared'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { CompanyHeader } from '@/components/Shared/CompanyHeader'
import { CompanyWorkspaceNav } from '@/components/Shared/CompanyWorkspaceNav'
import { InlineErrorBanner } from '@/components/Shared/InlineErrorBanner'
import { ScanningIndicator } from '@/components/Shared/ScanningIndicator'
import { WorkspaceSetupWizard } from '@/components/Shared/WorkspaceSetupWizard'
import { useScanPolling } from '@/hooks/useScanPolling'

interface EditProps {
  company: CompanyProfile
  workspaceSetup: WorkspaceSetup
  enrichment: CompanyEnrichment | null
  suggestedFields: string[]
}

// Type alias (not interface): usePage<T>'s PageProps constraint needs the
// implicit index signature only object-literal types carry.
type EditPageProps = SharedPageProps & {
  company: CompanyProfile
  errors?: Record<string, string>
}

interface ProfileFormData {
  name: string
  website: string
  careers_url: string
  contact_email: string
  description: string
}

type ProfileFieldName = keyof ProfileFormData

const SCAN_REVEAL_MS = 60_000

const PROFILE_FIELD_NAMES: readonly ProfileFieldName[] = [
  'name',
  'website',
  'careers_url',
  'contact_email',
  'description',
]

// Bridges each snake_case form field to its camelCase key on the proposed/company sources.
const PROPOSED_KEY_BY_FIELD: Record<ProfileFieldName, keyof CompanyEnrichmentProposed> = {
  name: 'name',
  website: 'website',
  careers_url: 'careersUrl',
  contact_email: 'contactEmail',
  description: 'description',
}

function seedProfileFormData(company: CompanyProfile, enrichment: CompanyEnrichment | null): ProfileFormData {
  const proposed = enrichment?.scanState === 'succeeded' && enrichment.hasProposals ? enrichment.proposed : null

  const seed = {} as ProfileFormData
  for (const field of PROFILE_FIELD_NAMES) {
    const sourceKey = PROPOSED_KEY_BY_FIELD[field]
    seed[field] = proposed?.[sourceKey] || company[sourceKey] || ''
  }
  return seed
}

interface ScanningPanelProps {
  onSkip: () => void
  startedAtMs: number
}

function ScanningPanel({ onSkip, startedAtMs }: ScanningPanelProps) {
  return (
    <div className="self-start rounded-card border border-border bg-background p-8 shadow-sm">
      <ScanningIndicator startedAtMs={startedAtMs} label="Scanning your company" />
      <p className="mt-3 max-w-[420px] font-sans text-[14px] leading-[1.55] text-muted-foreground">
        We're researching your company from public sources to pre-fill this form. This usually
        takes under a minute.
      </p>
      <Button type="button" variant="outline" onClick={onSkip} className="mt-6">
        Skip and fill manually
      </Button>
    </div>
  )
}

interface QuestionFieldProps {
  question: CompanyEnrichmentQuestion
  value: string
  onChange: (value: string) => void
  disabled: boolean
}

function QuestionField({ question, value, onChange, disabled }: QuestionFieldProps) {
  return (
    <div className="flex flex-col gap-[6px]">
      <label
        htmlFor={`enrichment-question-${question.id}`}
        className="font-sans text-[13px] font-medium text-foreground"
      >
        {question.question}
      </label>
      <Input
        id={`enrichment-question-${question.id}`}
        type={question.kind}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  )
}

export default function CompanyProfileEdit({ company, workspaceSetup, enrichment, suggestedFields }: EditProps) {
  const { errors: pageErrors } = usePage<EditPageProps>().props
  const firstTime = !workspaceSetup.profileConfirmed
  const setupComplete =
    workspaceSetup.profileConfirmed &&
    workspaceSetup.positionsCount > 0

  const form = useForm<ProfileFormData>(seedProfileFormData(company, enrichment))
  const { setData } = form
  const [touched, setTouched] = useState<Record<ProfileFieldName, boolean>>(
    () => Object.fromEntries(PROFILE_FIELD_NAMES.map((field) => [field, false])) as Record<ProfileFieldName, boolean>,
  )
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [optedOut, setOptedOut] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const [gaveUp, setGaveUp] = useState(false)
  const [scanStartedAtMs] = useState(() => Date.now())

  const scanState = enrichment?.scanState ?? null
  const anyFieldTouched = Object.values(touched).some(Boolean)
  const scanActive = scanState === 'pending' && !optedOut && !anyFieldTouched && !gaveUp
  const isScanning = scanActive && !revealed
  const showBackgroundScanNote = scanState === 'pending' && revealed && !gaveUp && !optedOut && !anyFieldTouched
  const showManualFallbackNotice = scanState === 'pending' && gaveUp && !optedOut && !anyFieldTouched

  const { timedOut } = useScanPolling({
    active: scanActive,
    startedAtMs: scanStartedAtMs,
    reloadKeys: ['enrichment', 'company', 'workspaceSetup', 'suggestedFields'],
  })

  useEffect(() => {
    if (timedOut) {setGaveUp(true)}
  }, [timedOut])

  useEffect(() => {
    if (scanState !== 'pending' || optedOut) {return}

    const timeout = setTimeout(() => setRevealed(true), SCAN_REVEAL_MS)

    return () => clearTimeout(timeout)
  }, [scanState, optedOut])

  useEffect(() => {
    if (scanState !== 'succeeded' || !enrichment?.hasProposals || !enrichment.proposed) {return}

    const proposed = enrichment.proposed
    const updates: Partial<ProfileFormData> = {}
    for (const field of PROFILE_FIELD_NAMES) {
      const value = proposed[PROPOSED_KEY_BY_FIELD[field]]
      if (!touched[field] && value) {updates[field] = value}
    }

    if (Object.keys(updates).length === 0) {return}

    setData((data) => ({ ...data, ...updates }))
  }, [scanState, enrichment?.hasProposals, enrichment?.proposed, touched, setData])

  function handleFieldChange(field: ProfileFieldName, value: string) {
    setData(field, value)
    setTouched((previous) => (previous[field] ? previous : { ...previous, [field]: true }))
  }

  const showSuggestion = (field: ProfileFieldName) => suggestedFields.includes(field) && !touched[field]

  function handleAnswerChange(questionId: string, value: string) {
    setAnswers((previous) => ({ ...previous, [questionId]: value }))
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    const submittedAnswers = Object.entries(answers)
      .filter(([, value]) => value.trim() !== '')
      .map(([id, answer]) => ({ id, answer }))

    form.transform((data) => ({ ...data, answers: submittedAnswers }))
    form.patch('/company/profile')
  }

  return (
    <>
      <Head title={firstTime ? 'Set up your company — Hyrin' : 'Company profile — Hyrin'} />

      <div className="min-h-screen bg-muted">
        <CompanyHeader />

        <main
          className="mx-auto max-w-[720px] px-6 py-[52px]"
        >
          <p className="mb-[14px] font-mono text-[11px] uppercase tracking-eyebrow text-primary">
            {firstTime ? 'Company setup' : 'Company settings'}
          </p>
          <h1 className="mb-[10px] font-heading text-[36px] font-bold leading-[1.06] tracking-[-0.025em] text-foreground">
            {firstTime ? 'Set up your company' : 'Edit company profile'}
          </h1>

          {setupComplete && <CompanyWorkspaceNav active="profile" />}

          <p className="mb-7 max-w-[640px] font-sans text-base leading-[1.55] text-muted-foreground">
            {firstTime
              ? 'Confirm your company details to get started — we seeded the name and website from your workspace domain.'
              : 'Keep your company information up to date so engineers can find you.'}
          </p>

          {!setupComplete && (
            <div className="mb-8">
              <WorkspaceSetupWizard setup={workspaceSetup} />
            </div>
          )}

          {isScanning ? (
              <ScanningPanel onSkip={() => setOptedOut(true)} startedAtMs={scanStartedAtMs} />
            ) : (
              <div className="rounded-card bg-background p-8 shadow-sm">
                <InlineErrorBanner message={pageErrors?.base} />

                {showBackgroundScanNote && (
                  <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-[8px] border border-border bg-muted px-4 py-3">
                    <ScanningIndicator startedAtMs={scanStartedAtMs} label="Scanning your company" />
                    <p className="font-sans text-[13px] text-muted-foreground">
                      Still scanning in the background — we'll fill in anything we find. Feel free to
                      start typing.
                    </p>
                  </div>
                )}

                {showManualFallbackNotice && (
                  <div className="mb-6 rounded-[8px] border border-border bg-muted px-4 py-3 font-sans text-[13px] text-muted-foreground">
                    Taking longer than expected — go ahead and fill this in manually.
                  </div>
                )}

                <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                  <div className="flex flex-col gap-[6px]">
                    <label
                      htmlFor="company-name"
                      className="font-sans text-[13px] font-medium text-foreground"
                    >
                      Company name
                    </label>
                    <Input
                      id="company-name"
                      type="text"
                      value={form.data.name}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      placeholder="Acme Corp"
                      disabled={form.processing}
                    />
                    {form.errors.name && (
                      <p className="font-sans text-[12px] text-destructive">{form.errors.name}</p>
                    )}
                  </div>

                  <div className="flex flex-col gap-[6px]">
                    <label
                      htmlFor="company-website"
                      className="font-sans text-[13px] font-medium text-foreground"
                    >
                      Website
                    </label>
                    <Input
                      id="company-website"
                      type="url"
                      required
                      value={form.data.website}
                      onChange={(e) => handleFieldChange('website', e.target.value)}
                      placeholder="https://acme.com"
                      disabled={form.processing}
                      aria-invalid={showSuggestion('website') || undefined}
                      className={
                        showSuggestion('website') ? 'border-destructive focus-visible:ring-destructive' : undefined
                      }
                    />
                    {form.errors.website && (
                      <p className="font-sans text-[12px] text-destructive">{form.errors.website}</p>
                    )}
                    {showSuggestion('website') && !form.errors.website && (
                      <p className="font-sans text-[12px] text-destructive">
                        Guessed from your domain — please verify.
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-[6px]">
                    <label
                      htmlFor="company-careers-url"
                      className="font-sans text-[13px] font-medium text-foreground"
                    >
                      Careers page
                    </label>
                    <Input
                      id="company-careers-url"
                      type="url"
                      value={form.data.careers_url}
                      onChange={(e) => handleFieldChange('careers_url', e.target.value)}
                      placeholder="https://acme.com/careers"
                      disabled={form.processing}
                      aria-invalid={showSuggestion('careers_url') || undefined}
                      className={
                        showSuggestion('careers_url') ? 'border-destructive focus-visible:ring-destructive' : undefined
                      }
                    />
                    {form.errors.careers_url && (
                      <p className="font-sans text-[12px] text-destructive">{form.errors.careers_url}</p>
                    )}
                    {showSuggestion('careers_url') && !form.errors.careers_url && (
                      <p className="font-sans text-[12px] text-destructive">
                        Guessed from your domain — please verify.
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-[6px]">
                    <label
                      htmlFor="company-contact-email"
                      className="font-sans text-[13px] font-medium text-foreground"
                    >
                      Contact email
                    </label>
                    <Input
                      id="company-contact-email"
                      type="email"
                      value={form.data.contact_email}
                      onChange={(e) => handleFieldChange('contact_email', e.target.value)}
                      placeholder="hello@acme.com"
                      disabled={form.processing}
                      aria-invalid={showSuggestion('contact_email') || undefined}
                      className={
                        showSuggestion('contact_email') ? 'border-destructive focus-visible:ring-destructive' : undefined
                      }
                    />
                    {form.errors.contact_email && (
                      <p className="font-sans text-[12px] text-destructive">{form.errors.contact_email}</p>
                    )}
                    {showSuggestion('contact_email') && !form.errors.contact_email && (
                      <p className="font-sans text-[12px] text-destructive">
                        Guessed from your domain — please verify.
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-[6px]">
                    <label
                      htmlFor="company-description"
                      className="font-sans text-[13px] font-medium text-foreground"
                    >
                      Description
                    </label>
                    <Textarea
                      id="company-description"
                      value={form.data.description}
                      onChange={(e) => handleFieldChange('description', e.target.value)}
                      placeholder="What does your company build?"
                      disabled={form.processing}
                    />
                    {form.errors.description && (
                      <p className="font-sans text-[12px] text-destructive">{form.errors.description}</p>
                    )}
                  </div>

                  {enrichment && enrichment.scanState === 'succeeded' && enrichment.questions.length > 0 && (
                    <div className="flex flex-col gap-6 border-t border-border pt-6">
                      <p className="font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
                        A couple more things
                      </p>
                      {enrichment.questions.map((question) => (
                        <QuestionField
                          key={question.id}
                          question={question}
                          value={answers[question.id] ?? ''}
                          onChange={(value) => handleAnswerChange(question.id, value)}
                          disabled={form.processing}
                        />
                      ))}
                    </div>
                  )}

                  <div className="flex justify-end pt-2">
                    <Button type="submit" disabled={form.processing}>
                      {form.processing ? 'Saving…' : firstTime ? 'Save and continue' : 'Save changes'}
                    </Button>
                  </div>
                </form>
              </div>
            )}
        </main>
      </div>
    </>
  )
}
